import React, { useEffect, useRef, useState } from 'react';
import { Button, Text, View } from "../../src/Components/Themed";
import * as Font from 'expo-font';
import type { AthleteProps, BestAvailableOrderProps, CompanyProps, EventOrderStatProps, EventProps, LeagueProps, MarketProps, MatchProps, MyPlayerProps, OrderProps, PlayerBalanceProps, PlayerReferralProps, PollProps, PromoProps, PublicPlayerProps, TeamProps, TournamentProps, TradeProps } from '../../src/types';
import { APIOverrides, Components, MarketComponents } from 'be-components';
import type { AuthStateProps } from '../../src/ApiOverrides/types';
import { MarketComponentApi, MarketComponentHelpers } from '../../src/MarketComponents/api';
import { ProfileApi } from '../../src/ProfileManager/api';
import { MarketAppHelpers } from './helpers';
import { ActivityIndicator, FlatList, Image, Linking, Modal, TouchableWithoutFeedback, useWindowDimensions, type LayoutChangeEvent } from 'react-native';
import { Icons } from '../../src/Components';
import { useColors } from '../../src/constants/useColors';
import AuthenticatorHolder from '../embed/components/AuthenticatorHolder';
import ProfileBar from './components/ProfileBar';
import LocationHolder from './components/LocationHolder';
import ProfileHolder from '../embed/components/ProfileHolder';
import OrderHolder from '../market2/components/OrderHolder';
import Pagination from '../../src/Components/Pagination';
import SocialOrderCard from '../../src/SocialComponents/SocialOrderCard';
import { MyOrdersHelpers } from '../../src/MarketComponents/components/MyOrderList/api';
import { AthleteImage } from '../../src/Components/Jerseys';
import moment from 'moment-mini';
import { FlashMarketApi } from '../../src/FlashMarket/api';
import FlashModal from './components/FlashModal';
import { AuthenticateApi } from '../../src/Authenticator/api';
import DesktopMarket from './components/DesktopMarket';
import type { BEClarityProps } from '../../src/Clarity';


type MarketAppProps = {
    league_ids?:string,
    team_ids?:string,
    clarity?:BEClarityProps,
    referral_code?:string,
    theme?:'props'|'futures'|'events',
    athlete_ids?:string,
    contest_type?:string,
    contest_ids?:string,
    company_id?:string
}

type WidgetDataProps = {
    widget_loading:boolean,
    company?:CompanyProps,
    active_toggle:string,
    flash_markets:PollProps[],
    events:EventProps[],
    active_contest:{ contest_type?:string, contest_id?:string },
    tournaments:TournamentProps[],
    matches:MatchProps[],
    teams:TeamProps[],
    athletes:AthleteProps[],
    best_available_orders:{ [key:string]:BestAvailableOrderProps[] },
    latest_trades:{ [key:string]:TradeProps[] },
    event_order_stats: { [key:string]:EventOrderStatProps[] }
}

type MyDataProps = {
    me_loading:boolean,
    me?:MyPlayerProps,
    my_balance?:PlayerBalanceProps,
    orders:OrderProps[]
}

type StaticDataProps = {
    static_loading:boolean,
    launched:boolean,
    company?:CompanyProps,
    init_event?:EventProps,
    init_player_market?:MarketProps,
    player_referral?:PlayerReferralProps,
    referrer?:PublicPlayerProps,
    referral_company?:CompanyProps,
    promo?:PromoProps,
    markets:MarketProps[],
    leagues:LeagueProps[],
    active_league?:LeagueProps
}
const MIN_DESKTOP_WIDTH = 600
const MIN_DESKTOP_HEIGHT = 600
const sections = [ 'my_data', 'toggle', 'props', 'flash', 'orders', 'events', 'tournaments', 'disclaimer' ]
//BEPostHog.generateClient('phc_VOgl6v8COVPyu4AERusWqK8HSD2dDju99qSa70G4nLl', {});
const MarketApp = ({ league_ids, team_ids, athlete_ids, company_id, referral_code, clarity }:MarketAppProps) => {
    const Colors = useColors();
    const { width, height } = useWindowDimensions();
    const [ scrolling_size, setScrollingSize ] = useState({ width: 0, height:0 });
    const mode = width > 600 ? 'desktop' : 'mobile'
    const desktop_width = width * 0.8 < MIN_DESKTOP_WIDTH ? MIN_DESKTOP_WIDTH : width * 0.8
    const desktop_height = height * 0.8 < MIN_DESKTOP_HEIGHT ? MIN_DESKTOP_HEIGHT : height * 0.8
    const [ show_authenticate, setShowAuthenticate ] = useState(false);
    const [ show_flash, setShowFlash ] = useState<string|undefined>(undefined);
    const [ show_location, setShowLocation ] = useState(false);
    const [itemHeight, setItemHeight] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const itemMeasured = useRef(false);
    const [ show_profile, setShowProfile ] = useState<{
        visible: boolean,
        walkthrough?:{ level: 'basic'|'verify' }
    }>({
        visible: false
    });
    const [ location, setLocation ] = useState<any | undefined>(undefined);
    const [ widget_size, setWidgetSize ] = useState({ width:0, height:0 });
    const [ auth_state, setAuthState ] = useState<AuthStateProps & { loaded:boolean }>({
        loaded: false,
        distinct_id: '',
        session_id: '',
        authenticated: false
    });
    const [ order_data, setOrderData ] = useState<{
        visible:boolean,
        order?:OrderProps
    }>({
        visible: false
    });

   
    const [ my_data, setMyData ] = useState<MyDataProps>({
        me_loading: false,
        orders: []
    });
    const { me_loading, orders, me, my_balance } = my_data;

    const [ static_data, setStaticData ] = useState<StaticDataProps>({
        static_loading: false,
        launched:false,
        leagues: [],
        markets: []
    });
    const { leagues, markets, launched, active_league  } = static_data;

    const [ widget_data, setWidgetData ] = useState<WidgetDataProps>({
        widget_loading: false,
        active_toggle: 'events',
        teams: [],
        active_contest: {},
        flash_markets:[],
        events: [],
        matches: [],
        tournaments: [],
        athletes: [],
        best_available_orders:{},
        event_order_stats: {},
        latest_trades:{}
    });

    const { widget_loading, flash_markets, active_contest, active_toggle, events, matches, tournaments, teams, athletes, best_available_orders, event_order_stats, latest_trades } = widget_data
    const order_event = events.find(e => order_data.order?.event_type == 'team' && e.event_id == order_data.order?.event_id);
    const order_tournament = tournaments.find(t => order_data.order?.event_type == 'tournament' && t.tournament_id == order_data.order?.event_id);
    const order_match = matches.find(m => order_data.order?.event_type == 'match' && m.match_id == order_data.order?.event_id);
    
    let wd = MarketAppHelpers.filterContests(events, tournaments, matches, league_ids, team_ids, athlete_ids)
    let prop_trades = MarketAppHelpers.filterPropTrades(markets, wd.events.map(e => e.event_id), latest_trades, active_contest.contest_id);
    const active_event = events.find(e => active_contest.contest_type == 'team' && active_contest.contest_id == e.event_id);

    const onItemLayout = (event: LayoutChangeEvent) => {
        if (!itemMeasured.current) {
            setItemHeight(event.nativeEvent.layout.height);
            itemMeasured.current = true; // Measure only once
        }
    };

    const setUpView = (num_items:number, array_length:number) => {
        let widget_height = widget_size.height * num_items
        const itemsPerPage = Math.max(num_items, itemHeight > 0 ? Math.floor(widget_height / itemHeight) : num_items);
        const totalPages = itemsPerPage > 0 ? Math.ceil(array_length / itemsPerPage): 1;
        const startIndex = currentPage * itemsPerPage;
        return { totalPages, startIndex, itemsPerPage }
    }
    const evd = setUpView(Math.max(1, Math.floor(widget_size.width / (350 + 20))), wd.events.length);
    const visible_events = evd.itemsPerPage > 0 ? wd.events.slice(evd.startIndex, evd.startIndex + evd.itemsPerPage) : [];
    const ovd = setUpView(Math.max(1, Math.floor(widget_size.width) / (300 + 20)), orders.length)
    const visible_orders = ovd.itemsPerPage > 0 ? orders.slice(ovd.startIndex, ovd.startIndex + ovd.itemsPerPage): [];

    const pvd = setUpView(Math.max(1, Math.floor(widget_size.width / (175 + 20))), prop_trades.length);
    const visible_props = pvd.itemsPerPage > 0 ? prop_trades.slice(pvd.startIndex, pvd.startIndex + pvd.itemsPerPage) : [];
    const total_pages = active_toggle == 'orders' ? ovd.totalPages : active_toggle == 'props' ? pvd.totalPages : evd.totalPages

    useEffect(() => {
        startUp();
    },[]);

    useEffect(() => {
        if(!launched){ return }
        if(clarity){ clarity.smartEvent('market_embed_launch') }
    },[launched])

    useEffect(() => {
        if(active_toggle != 'orders'){ return }
        refreshOrders()
    },[active_toggle]);

    useEffect(() => {
        //if(active_toggle != 'props'){ return }
        if(visible_props.length == 0){ return }
        loadAthletes()
    },[JSON.stringify(visible_props)]);

    const loadAthletes = async() => {
        let unique_ids = [ ...new Set(prop_trades.map(p => p.side_id)) ]
        let needed_ids = unique_ids.filter(id => !athletes.map(a => a.athlete_id.toString()).includes(id.toString()));
        const aths = await MarketComponentApi.getAthletesByIds(needed_ids);
        setWidgetData({ 
            ...widget_data, 
            athletes: athletes.filter(a => !aths.find(na => na.athlete_id == a.athlete_id)).concat(aths)
        })
    }

    const refreshOrders = async() => {
        const { event_ids, tournament_ids, match_ids } = MyOrdersHelpers.getContestsFromOrders(orders);
        const my_orders = await MarketComponentApi.getMyAction();
        setMyData({
            ...my_data,
            orders: my_orders
        })
        let needed_events = event_ids.filter(id => !events.find(e => e.event_id == id))
        let needed_tournaments = tournament_ids.filter(id => !tournaments.find(e => e.tournament_id == id))
        let need_matches = match_ids.filter(id => !matches.find(e => e.match_id == id));
        let evs = await MarketComponentApi.getEventsByEventIds(needed_events)
        let tourns = await MarketComponentApi.getTournamentsByTournamentIds(needed_tournaments);
        let maths = await MarketComponentApi.getMatchesByMatchIds(need_matches);
        setWidgetData({
            ...widget_data,
            events: events.concat(evs),
            tournaments: tournaments.concat(tourns),
            matches: matches.concat(maths)
        });
    }
    
    Font.useFonts({
        BarlowBold: require('../assets/fonts/Barlow-Bold.ttf'),
        BarlowSemiBold: require('../assets/fonts/Barlow-SemiBold.ttf'),
        BarlowRegular: require('../assets/fonts/Barlow-Regular.ttf'),
        BarlowLight: require('../assets/fonts/Barlow-Light.ttf'),
    });


    const startUp = async() => {
        const start_response = await APIOverrides.start({
            env: 'PRODUCTION_PROXY',
            no_cache: false
        });
        setAuthState({ ...start_response, loaded: true });
        //Set up the API!
        ProfileApi.setEnvironment();
        MarketComponentApi.setEnvironment();
        loadStaticData();
        loadWidgetData();
        if(start_response.authenticated){ loadMyData() }
    }

    const loadStaticData = async() => {
        AuthenticateApi.setEnvironment();
        setStaticData({ ...static_data, static_loading: true });
        const req_leagues = await MarketComponentApi.getLeagues();
        const req_markets = await MarketComponentApi.getMarkets();
        let comp = await AuthenticateApi.getCompanyById(company_id ?? '14');
        let ref_code = await AuthenticateApi.getReferralByCode(referral_code ?? 'kickoff');
        let active_leage = req_leagues.find(l => league_ids && l.league_id == league_ids[0])
        setStaticData({
            ...static_data,
            markets: req_markets,
            company: comp.company,
            player_referral: ref_code.player_referral,
            promo: ref_code.promo,
            referrer: ref_code.referrer,
            referral_company: ref_code.company,
            active_league:active_leage,
            leagues: req_leagues,
            static_loading: false
        })
    }

    const loadWidgetData = async() => {
        FlashMarketApi.setEnvironment();
        setWidgetData({ ...widget_data, widget_loading: true });
        let { matches, tournaments, events } = await MarketComponentApi.getBestAvailableOrders();
        let missing_tournaments = MarketComponentHelpers.getMissingTournaments(matches, tournaments);
        tournaments = tournaments.concat(await MarketComponentApi.getTournamentsByTournamentIds(missing_tournaments));
        const fb = MarketComponentHelpers.formatBestAvailable({ events, tournaments, matches });
        const a_ids = MarketComponentHelpers.extractAthleteIds(athlete_ids);
        let aths:AthleteProps[] = await MarketComponentApi.getAthletesByIds(a_ids);
        const fms = await FlashMarketApi.getActiveMarkets();
        setWidgetData({
            ...widget_data,
            widget_loading: false,
            events: events,
            matches: matches,
            flash_markets: fms,
            tournaments: tournaments,
            athletes: aths,
            event_order_stats: fb.order_stats,
            best_available_orders: fb.best_available,
            latest_trades: fb.latest_trades
        });
    }

    const loadMyData = async(session_id?:string) => {
        setMyData({ ...my_data, me_loading: true });
        const req_me = await ProfileApi.getMyDetails();
        //BEPostHog.EventActions.identify(req_me.player.player_id, { username: req_me.player.username })
        const req_pb = await ProfileApi.getMyBalance();
        const req_action = await MarketComponentApi.getMyAction();
        //BEPostHog.EventActions.identify(req_me.player.player_id, { username: req_me.player.username })
        if(clarity){ clarity.identify(req_me.player.player_id, session_id) }
        setMyData({
            ...my_data,
            me: req_me.player,
            my_balance: req_pb,
            orders: req_action,
            me_loading: false
        })
    }

    const renderAthleteProps = (data:{ item:TradeProps, index:number, compact?:boolean }) => {
        const event = wd.events.find(e => e.event_id == data.item.event_id);
        if(!event){ return <></> }
        const market = markets.find(m => m.market_id == data.item.market_id);
        if(!market){ return <></> }
        const athlete = athletes.find(a => a.athlete_id == data.item.side_id);
        if(!athlete){ return <></> }
        const league = leagues.find(l => l.league_id == athlete.league_id)
        let best_available = best_available_orders[`team:${data.item.event_id}`] ?? []
        best_available = best_available.filter(a => a.market_id == data.item.market_id && a.side_type == 'athlete' && a.side_id == data.item.side_id);
        let event_trades = latest_trades[`team:${data.item.event_id}`] ?? []
        event_trades = event_trades.filter(a => a.market_id == data.item.market_id && a.side_type == 'athlete' && a.side_id == data.item.side_id);
        let order_stats = event_order_stats[`team:${data.item.event_id}`] ?? []
        order_stats = order_stats.filter(a => a.market_id == data.item.market_id && a.side_type == 'athlete' && a.side_id == data.item.side_id);
        if(data.compact){
            return (
                <Button transparent key={data.item.event_id.toString()} style={{ flexDirection:'row', alignItems:'center', padding:5, margin:5, borderRadius:0, borderLeftWidth:1, borderColor:Colors.borders.light }} onPress={() => {
                    setStaticData({ ...static_data, launched: true, init_event: event, init_player_market: market });
                    //BEPostHog.EventActions.captureEvent('launched', { company_id, source:'embed', source_type:'market', location: window?.location?.href })
                }}>
                    <AthleteImage
                        athlete={athlete}
                        size={28}
                        league={league}
                    />
                    
                    <View transparent style={{ flex:1, marginLeft:10 }}>
                        <Text theme='h2'>{athlete.first_name} {athlete.last_name}</Text>
                        <Text theme='light' style={{marginTop:3}}>{market.stat_label ?? market.stat}</Text>
                    </View>
                    <View transparent style={{ marginLeft:10 }}>
                        <Text theme='h1' color={Colors.text.highlight}>{market.var_1_required ? `${MarketComponentHelpers.getVar1Label(market, data.item.var_1)}`: MarketComponentHelpers.getOddsLabel(data.item.odds)}</Text>
                        {market.var_1_required ?
                        <Text theme='description' style={{ marginTop:3 }}>{MarketComponentHelpers.getOddsLabel(data.item.odds)}</Text>
                        :<></>}
                    </View>
                </Button>
            )
        }
        return (
        <View key={data.item.trade_id.toString()} style={{ padding:5, flexBasis: 50, flexGrow:1, minWidth:180, maxWidth:500 }} onLayout={onItemLayout}>
            <MarketComponents.AthleteMarket
                event={event}
                float
                direction='vertical'
                show_event
                style={{ flex:1, justifyContent:'center' }}
                best_available_orders={best_available}
                event_order_stats={order_stats}
                latest_trades={event_trades}
                onOrder={(order) => setOrderData({ visible:true, order })}
                event_type='team'
                onTradeLongPress={() => console.log('')}
                onView={(data) => console.log(data)}
                hide_liquidity
                market={market}
                default_price_view='best_available'
                athlete={athlete}
            />
        </View>
        )
    }

    const renderEvents = (data:{ item:EventProps, index:number, compact?:boolean, ultra_compact?:boolean }) => {
        const league = leagues.find(l => l.league_id == data.item.league_id);
        const best_available = best_available_orders[`team:${data.item.event_id}`] ?? []
        const event_trades = latest_trades[`team:${data.item.event_id}`] ?? []
        const order_stats = event_order_stats[`team:${data.item.event_id}`] ?? []
        const is_live = moment().isAfter(moment(data.item.scheduled_datetime)) ? true : false
        if(data.compact){
            let primary_markets = markets.filter(m => m.primary_market);
            let supported_primary_markets = primary_markets.filter(m => data.item.supported_markets?.map(sm => sm.market_id.toString()).includes(m.market_id.toString()));
            let default_market = supported_primary_markets.find(m => m.type == 'Winner');
            if(!default_market){ default_market = supported_primary_markets.find(m => m.type == 'Spread') }
            if(!default_market){
                default_market = supported_primary_markets[0]
            }
            const market_1_trade = event_trades.find(t => t.market_id == default_market?.market_id && t.market_type == 'FOR_MONEY' && t.side == default_market?.trade_side);
            if(data.ultra_compact){
                return (
                    <Button transparent key={data.item.event_id.toString()} style={{ padding:0, borderRadius:0, borderLeftWidth:1, borderColor:Colors.borders.light }} onPress={() => setStaticData({ ...static_data, launched: true, init_event:data.item })}>
                        <View transparent style={{ padding:4 }}>
                            <Text theme='action' size={12} textAlign='center'>{['created','scheduled'].includes(data.item.time_detail ?? '') ? moment(data.item.scheduled_datetime).format('ddd hh:mm a') : data.item.time_detail}</Text>
                        </View>
                        <View transparent style={{ flexDirection:'row' }}>
                            <View transparent nativeID='away' style={{ padding:4 }}>
                                <Image
                                    source={{ uri: data.item.away?.image?.url }}
                                    style={{ height: 30, width: 30, borderRadius:100 }}
                                    resizeMode='cover'
                                />
                                {is_live ?
                                <View transparent style={{ position:'absolute', justifyContent:'flex-end', alignItems:'center', top:0, left:0, right:0, bottom:0 }}>
                                    <View style={{ height:20, width:30, borderRadius:0, borderTopRightRadius:8, borderTopLeftRadius:8, justifyContent:'center', alignItems:'center' }}>
                                        <Text theme='h1' textAlign='center'>{data.item.away_team_score}</Text>
                                    </View>
                                </View>
                                :<></>}
                            </View>
                            <View transparent nativeID='home' style={{ marginLeft:5, padding:4 }}>
                                <Image
                                    source={{ uri: data.item.home?.image?.url }}
                                    style={{ height: 30, width: 30, borderRadius:100 }}
                                    resizeMode='cover'
                                />
                                {is_live ?
                                <View transparent style={{ position:'absolute', justifyContent:'flex-end', alignItems:'center', top:0, left:0, right:0, bottom:0 }}>
                                    <View style={{ height:20, width:30, borderRadius:0, borderTopRightRadius:8, borderTopLeftRadius:8, justifyContent:'center', alignItems:'center' }}>
                                        <Text theme='h1' textAlign='center'>{data.item.home_team_score}</Text>
                                    </View>
                                </View>
                                :<></>}
                            </View>
                        </View>
                    </Button>
                )
            }
            return (
                <Button transparent key={data.item.event_id.toString()} style={{ flexDirection:'row', alignItems:'center', padding:0, paddingRight:10, paddingLeft:10, margin:5, borderRadius:0, borderLeftWidth:1, borderColor:Colors.borders.light }} onPress={() => setStaticData({ ...static_data, launched: true, init_event:data.item })}>
                    <View transparent style={{ flex:1, marginLeft:10 }}>
                        <Text theme='h2'>{data.item.event_title}</Text>
                        <Text theme='light' style={{marginTop:3}}>{['scheduled','created'].includes(data.item.time_detail??'')?moment(data.item.scheduled_datetime).format('MMM DD YYYY hh:mm a'):data.item.time_detail}</Text>
                    </View>
                    {default_market && market_1_trade ?
                    <View transparent style={{ marginLeft:10 }}>
                        <Text theme='h1' color={Colors.text.highlight} style={{ marginTop:3 }}>{default_market.var_1_required ? `${MarketComponentHelpers.getVar1Label(default_market, market_1_trade.var_1)}`: MarketComponentHelpers.getOddsLabel(market_1_trade.odds)}</Text>
                    </View>
                    :<></>}
                </Button>
            )
        }
        const ev_markets = flash_markets.filter(fm => fm.contest_type == 'team' && fm.contest_id == data.item.event_id);
        return (
            <View key={data.item.event_id.toString()} style={{ padding:5, flexBasis: 50, flexGrow:1, minWidth:350, maxWidth:500 }} onLayout={onItemLayout}>
                <MarketComponents.TeamEventMarket
                    event={data.item}
                    league={league}
                    float
                    style={{ flexGrow:1 }}
                    hide_non_primary
                    onFlashSelect={(poll_id) => setShowFlash(poll_id)}
                    flash_markets={ev_markets}
                    //init_expanded
                    onCompetitionSelect={() => console.log('')}
                    onOrder={(order) => setOrderData({ visible: true, order })}
                    onSquaresSelect={(sq) => console.log(sq)}
                    onTradeLongPress={(t) => console.log(t)}
                    onView={(data) => console.log(data)}
                    hide_liquidity
                    
                    best_available_orders={best_available}
                    latest_trades={event_trades}
                    markets={markets}
                    event_order_stats={order_stats}
                    default_price_view='best_available'
                    onViewAdditionalMarkets={(event_id, event_type) => console.log(event_id, event_type)}
                />
            </View>
        )
    }

    const handleComplete = () => {
        if(launched){ return }
        //Lets check if there was an active event
        let active_index = wd.events.findIndex(e => e.event_id == active_contest.contest_id);
        let next_index = active_index + 1
        let next_contest = wd.events[next_index]
        if(!next_contest){
            setWidgetData({ ...widget_data, active_toggle: 'events', active_contest: {} })
        } else {
            setWidgetData({ ...widget_data, active_toggle: 'props', active_contest: { contest_type:'team', contest_id: next_contest?.event_id } })
        }
    }

    const renderOrders = (data:{item:OrderProps, index:number}) => {
        if(!me){ return <></> }
        let { title, time, league, team, athlete } = MarketAppHelpers.getContestDetails(data.item, events, tournaments, matches, athletes, teams, leagues);
        return (
            <View key={data.item.order_id.toString()} style={{ padding:10, flexBasis: 50, flexGrow:1, minWidth:300, maxWidth:500 }} onLayout={onItemLayout}>
                <SocialOrderCard
                    player={me}
                    league={league}
                    team={team}
                    //maxWidth={maxWidth}
                    athlete={athlete}
                    show_amounts
                    contest_title={title}
                    contest_time={time}
                    order={data.item}
                    //onFadeOrder={onFadeOrder}
                    //onCopyOrder={onCopyOrder}
                />
            </View>
        )
    }

    const renderSections = (data:{item:string, index:number}) => {
        switch(data.item){
            case 'props':
                if(active_toggle != 'props'){ return <></> }
                return (
                    <View style={{ flexDirection:'row', flexWrap:'wrap' }}>
                        {visible_props.map((item, index) => {
                            return renderAthleteProps({ item, index })
                        })}
                    </View>
                )
            case 'orders':
                if(active_toggle != 'orders'){ return <></> }
                return (
                    <View style={{ flexDirection:'row', flexWrap:'wrap', justifyContent:'center' }}>
                        {visible_orders.map((item, index) => {
                            return renderOrders({ item, index })
                        })}
                        
                    </View>
                )
            case 'events': 
                if(active_toggle != 'events'){ return <></> }
                return (
                    <View style={{ flexDirection:'row', flexWrap:'wrap', justifyContent:'center' }}>
                        {visible_events.map((item, index) => {
                            return renderEvents({ item, index })
                        })}
                        
                    </View>
                )

            default: return <></>
        }
    }
    return (
        <View>
            <View type='header' style={{ width:'100%', flexDirection:'row', alignItems:'center', padding:2, borderRadius:0, justifyContent:'center' }}>
                <Button transparent style={{ padding:me?5:0, justifyContent:'center', alignItems:'center' }} onPress={() => setStaticData({ ...static_data, launched: true })}>
                    {me?
                    <Image
                        source={{ uri: me.profile_pic && me.profile_pic != '' ? me.profile_pic : 'https://res.cloudinary.com/hoabts6mc/image/upload/v1722453927/default_man_n96ofq.webp' }}
                        style={{ height:35, width:35, borderRadius:100 }}
                        resizeMode='cover'
                    />
                    : me_loading ?
                        <ActivityIndicator size={'small'} color={Colors.text.h1} />
                    : width > 600 ?
                    <View transparent style={{ flexDirection:'row', alignItems:'center', padding:5, borderRightWidth:1, borderColor:Colors.borders.light }}>
                        <View transparent style={{ flexDirection:'row', alignItems:'center', marginRight:10 }}>
                            <Icons.BettorEdgeIcon size={14} color={Colors.text.h1} />
                            <View transparent style={{ marginLeft:10 }}>
                                <Text theme='light' color={Colors.text.h1}>Lines Powered By</Text>
                                <Text theme='h2' color={Colors.text.h1} style={{ marginTop:3 }}>Bettoredge</Text>
                            </View>
                        </View>
                        <Button
                            title={static_data.player_referral?.description ?? 'Get Started'}
                            type='success'
                            style={{ padding:10 }}
                            onPress={() => {
                                setStaticData({ ...static_data, launched:true })
                                setTimeout(() => setShowAuthenticate(true), 250 )
                            }}
                        />
                    </View>         
                    :<></>}
                </Button>
                {active_event ?
                <View transparent style={{ flexDirection:'row', borderRightWidth:1, borderColor:Colors.borders.light }}>
                    {renderEvents({ item: active_event, index:0, compact: true, ultra_compact: true })}
                </View>
                :<></>}
                <View transparent style={{ flex:1, marginRight:10, marginLeft:5 }} onLayout={(ev) => {
                    const { width, height } = ev.nativeEvent.layout;
                    setScrollingSize({ width, height })
                }}>
                    {active_toggle == 'props' && prop_trades.length > 0 ?
                    <Components.AutoScrollingFlatList
                        data={prop_trades} 
                        resetKey={`embed_props`}
                        renderItem={({ item, index }:any) => renderAthleteProps({ item, index, compact:true })}
                        speed={60}
                        width={scrolling_size.width}
                        horizontal
                        minDuration={10000}
                        onCompleteScroll={() => handleComplete()}
                    />
                    :
                    <Components.AutoScrollingFlatList
                        data={wd.events}
                        key={'embed_events'}
                        renderItem={({ item, index }:any) => renderEvents({ item, index, compact:true })}
                        speed={60}
                        width={scrolling_size.width}
                        horizontal
                        minDuration={10000}
                        onCompleteScroll={() => handleComplete()}
                    />
                    }
                </View>
                {active_toggle == 'events' && active_league ?
                <View transparent>
                    <Image
                        source={{ uri: active_league.league_image }}
                        style={{ height:35, width:35 }}
                        resizeMode='cover'
                    />
                </View>
                :<></>}
            </View>
            <Modal
                style={{ flex:1, height, width }}
                transparent
                animationType={mode=='desktop' ? 'fade' : 'slide'}
                visible={launched}
            >
            <TouchableWithoutFeedback style={{ flex:1 }} onPress={() => setStaticData({ ...static_data, launched: false })}>
                <View type='blur' style={mode=='desktop' ? { flex:1, justifyContent:'center', alignItems:'center' }:{ flex:1 }}>
                    <TouchableWithoutFeedback>
                    <View style={mode=='desktop'? { borderRadius:8, width:desktop_width, height:desktop_height }:{ flex:1, margin:10, borderRadius:8 }}>
                    <View style={{ flex:1, borderRadius:8 }}>
                        <View transparent style={{ borderBottomWidth:1, borderColor:Colors.text.success }}>
                            <ProfileBar
                                me={me}
                                loading={me_loading}
                                orders={orders}
                                mode={mode}
                                active_toggle={active_toggle}
                                onViewFlash={() => {
                                    setWidgetData({ ...widget_data, active_toggle: 'flash' })
                                    setCurrentPage(0);
                                }}
                                onViewOrders={() => {
                                    setWidgetData({ ...widget_data, active_toggle: 'orders' })
                                    setCurrentPage(0);
                                }}
                                my_balance={my_balance}
                                onViewProps={() => {
                                    setWidgetData({ ...widget_data, active_toggle: 'props' })
                                    setCurrentPage(0)
                                }}
                                onViewEvents={() => {
                                    setWidgetData({ ...widget_data, active_toggle: 'events' })
                                    setCurrentPage(0)
                                }}
                                onRequestAuthenticate={() => setShowAuthenticate(true)}
                                onViewProfile={() => setShowProfile({ visible: true })}    
                            />
                        </View>
                        {(mode == 'mobile' || active_toggle == 'orders') && total_pages > 1 ?
                        <Pagination
                            offset={currentPage}
                            onNext={() => setCurrentPage(currentPage + 1)}
                            pages={total_pages}
                            onSelectPage={(page) => setCurrentPage(page)}
                            onPrevious={() => setCurrentPage(currentPage - 1)}
                        />
                        :<></>}
                        <View style={{ flex:1 }} onLayout={(ev) => {
                            const { height, width } = ev.nativeEvent.layout;
                            setWidgetSize({ height, width })
                        }}>
                            {widget_loading ?
                            <ActivityIndicator
                                size='large'
                                color={Colors.text.h1}
                            />
                            :<></>}
                            {mode == 'mobile' ?
                            <FlatList
                                data={sections}
                                key={'market_app_sections'}
                                keyExtractor={(item) => item}
                                renderItem={renderSections}
                            />
                            :
                            <View style={{ flex:1 }}>
                                {active_toggle == 'orders' ?
                                <View style={{ flexDirection:'row', flexWrap:'wrap', justifyContent:'center' }}>
                                    {visible_orders.map((item, index) => {
                                        return renderOrders({ item, index })
                                    })}
                                    
                                </View>
                                :
                                <DesktopMarket
                                    league={leagues.find(l => league_ids && l.league_id == league_ids[0])}
                                    markets={markets}
                                    init_event={static_data.init_event}
                                    init_player_market={static_data.init_player_market}
                                    event_order_stats={event_order_stats}
                                    flash_markets={flash_markets}
                                    onFlashSelect={(poll_id) => setShowFlash(poll_id)}
                                    best_available_orders={best_available_orders}
                                    onOrder={(order) => setOrderData({ visible:true, order })}
                                />
                            }
                            </View>
                            }
                        </View>
                        <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomRightRadius:8, borderBottomLeftRadius:8 }}>
                            <Button transparent style={{ flexDirection:'row', alignItems:'center', padding:0, flex:1 }} onPress={() => Linking.openURL(`https://play.bettoredge.com?refer=${referral_code ?? 'kickoff'}&utm_source=market_embed`)}>
                                <Icons.BettorEdgeIcon size={18} color={Colors.text.h1} />
                                <View transparent style={{ flex:1, marginLeft:10, borderLeftWidth:1, borderColor:Colors.borders.light, paddingLeft:10 }}>
                                    <Text theme='light'>Powered By</Text>
                                    <Text theme='h1' style={{ marginTop:3 }}>BettorEdge</Text>
                                </View>
                            </Button>
                            <Button
                                float
                                title_color={Colors.text.error}
                                title='X'
                                style={{ height:40, width:40, borderRadius:100, justifyContent:'center', alignItems:'center', padding:0}}
                                onPress={() => setStaticData({ ...static_data, launched:false })}
                            />
                        </View>
                        {auth_state.loaded ?
                        <LocationHolder
                            player_id={me?.player_id}
                            onClose={() => setShowLocation(false)}
                            visible={show_location}
                            mode={mode}
                            onLocationUpdate={(location) => {
                                if(location?.location?.legal_ind && show_location){ setShowLocation(false) }
                                setLocation(location)
                            }}
                        />
                        :<></>}
                        {auth_state.loaded ?
                        <FlashModal
                            poll_id={show_flash}
                            mode={mode}
                            location={location?.location}
                            onClose={() => setShowFlash(undefined)}
                            onRequestAuthenticate={() => {
                                setShowFlash(undefined);
                                setShowAuthenticate(true);
                            }}
                            me={me}
                            onRequestLocation={() => {
                                setShowFlash(undefined);
                                if(clarity){ clarity.smartEvent('show_location') }
                                setShowLocation(true);
                            }}
                        />
                        :<></>}
                        {me ?
                        <ProfileHolder
                            me={me}
                            onClose={() => setShowProfile({ ...show_profile, visible: false })}
                            mode={mode}
                            
                            visible={show_profile.visible}
                            onLogout={async() => {
                                await APIOverrides.logoutApp();
                                setAuthState({
                                    distinct_id: auth_state.distinct_id,
                                    session_id: auth_state.session_id,
                                    authenticated: false,
                                    loaded: true
                                });
                                setMyData({ orders: [], me_loading:false })
                            }}
                        />
                        :<></>}
                        {order_data.visible && order_data.order ?
                        <OrderHolder
                            visible={order_data.visible}
                            event={order_event}
                            tournament={order_tournament}
                            match={order_match}
                            order={order_data.order}
                            mode={mode}
                            company_id={company_id}
                            teams={teams}
                            athletes={athletes}
                            markets={markets}
                            me={me}
                            onRequestLocation={() => {
                                setOrderData({ visible:false })
                                setShowLocation(true);
                            }}
                            onCreatedOrder={(order) => {
                                setMyData({
                                    ...my_data,
                                    orders: orders.filter(o => o.order_id != order.order_id).concat(order)
                                });
                            }}
                            onRequestAuthenticate={() => {
                                setOrderData({ visible:false })
                                setShowAuthenticate(true)
                            }}
                            my_balance={my_balance}
                            location={location}
                            onClose={(data) => {
                                setOrderData({ visible: false })
                                if(data?.view_order){
                                    setWidgetData({ ...widget_data, active_toggle:'orders' })
                                }
                            }}
                        />
                        :<></>}
                        <AuthenticatorHolder
                            visible={show_authenticate}
                            mode={mode}
                            company_id={company_id ?? '1'}
                            referral_code={referral_code}
                            onClose={() => setShowAuthenticate(false)}
                            onAuthenticated={async(auth_response) => {
                                await APIOverrides.authenticateApp({ ...auth_response, authenticated:true, distinct_id: auth_state.distinct_id, session_id: auth_state.session_id}, { cache: true })
                                setAuthState({
                                    ...auth_state,
                                    authenticated:true,
                                    ...auth_response
                                });
                                loadMyData(auth_state.session_id);
                                setShowAuthenticate(false);
                            }}

                        />
                    </View>
                </View>
                </TouchableWithoutFeedback>
                </View>
                </TouchableWithoutFeedback>
            </Modal>
        </View>
       
    )
}


export default MarketApp